import { useEffect, useState } from 'react';

// https://css-tricks.com/building-progress-ring-quickly/
export function RingProgressBar({
	strokeColor,
	color,
	radius,
	stroke,
	progress,
	text,
	className,
	inset = true,
	entrance = false,
}: {
	strokeColor: string;
	color: string;
	radius: number;
	stroke: number;
	progress: number;
	text?: string | number;
	/** Also carries the negative margin reclaiming `inset`'s padding, which scales with the size set here. */
	className?: string;
	/** ~11% padding per side for neighbours to overlap; `false` crops to the stroke, for use as an icon. */
	inset?: boolean;
	/** Sweeps in from zero on mount; pair with `AnimateNumber`'s entrance, or the two read as different figures. */
	entrance?: boolean;
}) {
	// A mount effect's update can share the mount's paint, leaving no start value; a frame callback cannot.
	const [swept, setSwept] = useState(!entrance);
	useEffect(() => {
		if (!entrance) return undefined;
		const frame = requestAnimationFrame(() => setSwept(true));
		return () => cancelAnimationFrame(frame);
	}, [entrance]);

	const normalizedRadius = radius - stroke * 2;
	const circumference = normalizedRadius * 2 * Math.PI;
	const strokeDashoffset = circumference - ((swept ? progress : 0) / 100) * circumference;

	const painted = normalizedRadius + stroke / 2;
	const viewBox = inset
		? `0 0 ${radius * 2} ${radius * 2}`
		: `${radius - painted} ${radius - painted} ${painted * 2} ${painted * 2}`;

	return (
		<svg height={radius * 2} width={radius * 2} viewBox={viewBox} className={className}>
			<circle
				stroke={strokeColor}
				fill="transparent"
				className="opacity-35"
				strokeWidth={stroke}
				r={normalizedRadius}
				cx={radius}
				cy={radius}
			/>
			<circle
				className="-rotate-90 transition-[stroke-dashoffset] duration-600"
				stroke={color}
				fill="transparent"
				strokeWidth={stroke}
				strokeDasharray={`${circumference} ${circumference}`}
				// Not `origin-center`: it resolves against the viewBox, which the cropped `inset={false}` case moves.
				style={{ strokeDashoffset, transformOrigin: `${radius}px ${radius}px` }}
				r={normalizedRadius}
				cx={radius}
				cy={radius}
			/>
			{text != null && (
				<text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="22" fill="rgb(var(--text))">
					{text}
				</text>
			)}
		</svg>
	);
}
